import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase/client";
import { useToast } from "@/hooks/use-toast";

export default function Register() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [selectedPlanoId, setSelectedPlanoId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    telefone: "",
    password: "",
    confirmPassword: ""
  });

  // Verificar se há um plano selecionado na URL
  useEffect(() => {
    const planoParam = searchParams.get("plano");
    if (planoParam) {
      const planoId = parseInt(planoParam, 10);
      if (!isNaN(planoId)) {
        setSelectedPlanoId(planoId);
      }
    }
  }, [searchParams]);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "Erro",
        description: "Email inválido",
        variant: "destructive",
      });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Erro",
        description: "As senhas não coincidem",
        variant: "destructive",
      });
      return;
    }

    if (formData.password.length < 6) {
      toast({
        title: "Erro",
        description: "A senha deve ter pelo menos 6 caracteres",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // Step 1: Create user in Supabase Auth
      // Note: We need to auto-confirm email for immediate access
      // This requires email confirmation to be disabled in Supabase Dashboard
      // OR we can use admin API to confirm after creation
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.name,
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          // Try to auto-confirm (only works if email confirmation is disabled)
        },
      });

      if (authError) {
        throw authError;
      }

      if (!authData.user) {
        throw new Error("Failed to create user");
      }

      // Step 2: Try to get session, but don't fail if not available
      // We'll use user_id as fallback in the Edge Function
      let session = authData.session;
      
      // If no session in response, try to get it (but don't fail)
      if (!session) {
        const { data: sessionData } = await supabase.auth.getSession();
        session = sessionData?.session || null;
      }

      // Note: We don't try signIn here because the user might not be confirmed yet
      // The Edge Function will accept user_id as an alternative authentication method

      // Step 3: Call Edge Function to create tenant (empresa + perfil)
      // We always include user_id - the Edge Function will validate it using service role
      // If a plan is selected and it's Free, pass it to create-tenant
      const createTenantBody: {
        nome: string;
        email: string;
        telefone?: string;
        user_id: string;
        plano_id?: number;
      } = {
        nome: formData.company,
        email: formData.email,
        telefone: formData.telefone || undefined,
        user_id: authData.user.id, // Always include user_id for validation
      };
      
      // Se há um plano selecionado, verificar se é Free para passar ao create-tenant
      if (selectedPlanoId) {
        const { data: planData } = await supabase
          .from("planos")
          .select("nome, preco_mensal, is_master_only")
          .eq("id", selectedPlanoId)
          .eq("is_master_only", false) // Não permite selecionar plano master
          .eq("is_active", true)
          .single();
        
        const isFreePlan = planData?.nome === "Free" || 
                          planData?.nome === "Control IA Free" ||
                          Number(planData?.preco_mensal) === 0;
        
        // Se for Free, passar o plano_id para o create-tenant
        if (isFreePlan) {
          createTenantBody.plano_id = selectedPlanoId;
        }
        // Se não for Free, não passar - o create-tenant vai usar o Free padrão
        // e depois vamos redirecionar para checkout
      }
      
      const { data: tenantData, error: tenantError } = await supabase.functions.invoke(
        "create-tenant",
        {
          body: createTenantBody,
          headers: session ? {
            Authorization: `Bearer ${session.access_token}`,
          } : {},
        }
      );

      if (tenantError) {
        console.error("Tenant creation error:", tenantError);
        // If tenant creation fails, we should clean up the auth user
        // But for now, just throw the error
        const errorMessage = tenantError.message || 
          tenantError.error || 
          "Falha ao criar empresa. Tente novamente.";
        
        // Check if it's a duplicate empresa error
        if (tenantError.status === 409 || errorMessage.includes("já cadastrada") || errorMessage.includes("já existe")) {
          throw new Error(
            errorMessage + " Se você pertence a esta empresa, peça um convite ao administrador."
          );
        }
        
        throw new Error(errorMessage);
      }

      if (!tenantData?.success) {
        console.error("Tenant creation failed:", tenantData);
        const errorMessage = tenantData?.error || 
          tenantData?.message || 
          "Falha ao criar empresa. Verifique os dados e tente novamente.";
        
        // Check if it's a duplicate empresa error
        if (tenantData?.empresa_existente || errorMessage.includes("já cadastrada") || errorMessage.includes("já existe")) {
          throw new Error(
            errorMessage + " Se você pertence a esta empresa, peça um convite ao administrador."
          );
        }
        
        throw new Error(errorMessage);
      }

      // Se há um plano selecionado, verificar se é Free ou pago
      if (selectedPlanoId) {
        // Buscar informações do plano (exclui planos master-only)
        const { data: planData } = await supabase
          .from("planos")
          .select("nome, preco_mensal, is_master_only")
          .eq("id", selectedPlanoId)
          .eq("is_master_only", false) // Não permite selecionar plano master
          .eq("is_active", true)
          .single();

        const isFreePlan = planData?.nome === "Free" || 
                          planData?.nome === "Control IA Free" ||
                          Number(planData?.preco_mensal) === 0;

        if (!isFreePlan) {
          // Plano pago - redirecionar para checkout
          toast({
            title: "Conta criada!",
            description: "Redirecionando para o checkout...",
          });

          // Aguardar um pouco para garantir que a sessão está ativa
          await new Promise((resolve) => setTimeout(resolve, 1000));

          // Obter a sessão atualizada
          const { data: { session: currentSession } } = await supabase.auth.getSession();
          
          if (currentSession?.user) {
            // Obter empresa_id do perfil criado
            const { data: perfil } = await supabase
              .from("perfis")
              .select("empresa_id")
              .eq("auth_user_id", currentSession.user.id)
              .single();

            if (perfil?.empresa_id) {
              // Criar sessão de checkout
              const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke(
                "create-checkout-session",
                {
                  body: {
                    plano_id: selectedPlanoId,
                    empresa_id: perfil.empresa_id,
                  },
                  headers: {
                    Authorization: `Bearer ${currentSession.access_token}`,
                  },
                }
              );

              if (checkoutError || !checkoutData?.url) {
                console.error("Checkout error:", checkoutError);
                // Se falhar, redirecionar para dashboard com aba de Faturamento
                const role = tenantData.perfil?.role || "user";
                if (role === "master") {
                  navigate("/dashboard/master");
                } else if (role === "admin") {
                  navigate("/dashboard/admin?tab=faturamento");
                } else {
                  navigate("/dashboard/colaborador");
                }
              } else {
                // Redirecionar para checkout do Stripe
                window.location.href = checkoutData.url;
                return;
              }
            }
          }
        }
        // Se for Free, continua o fluxo normal (vai para dashboard)
      }

      toast({
        title: "Sucesso!",
        description: "Conta criada com sucesso. Redirecionando...",
      });

      // Redirect based on role - sempre para admin com aba de Faturamento
      const role = tenantData.perfil?.role || "user";
      if (role === "master") {
        navigate("/dashboard/master");
      } else if (role === "admin") {
        // Redirecionar para a aba de Faturamento para contratar plano
        navigate("/dashboard/admin?tab=faturamento");
      } else {
        navigate("/dashboard/colaborador");
      }
    } catch (error: any) {
      console.error("Registration error:", error);
      toast({
        title: "Erro ao criar conta",
        description: error.message || "Ocorreu um erro inesperado",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <Link to="/">
            <h1 className="text-3xl font-bold bg-hero-gradient bg-clip-text text-transparent">
              Orbita Control IA
            </h1>
          </Link>
        </div>
        
        <Card className="border-border">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl">Criar Conta</CardTitle>
            <CardDescription>
              {selectedPlanoId 
                ? "Preencha os dados para finalizar sua assinatura"
                : "Preencha os dados para começar gratuitamente"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome Completo</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="João Silva"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="bg-input border-border"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Empresa</Label>
                <Input
                  id="company"
                  type="text"
                  placeholder="Minha Empresa Ltda"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  required
                  className="bg-input border-border"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className="bg-input border-border"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefone">Telefone (Opcional)</Label>
                <Input
                  id="telefone"
                  type="tel"
                  placeholder="(11) 99999-9999"
                  value={formData.telefone}
                  onChange={(e) => setFormData({...formData, telefone: e.target.value})}
                  className="bg-input border-border"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  className="bg-input border-border"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmar Senha</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  required
                  className="bg-input border-border"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow-primary"
                disabled={loading}
              >
                {loading ? "Criando conta..." : "Criar Conta"}
              </Button>
            </form>
            
            <div className="mt-4 text-center text-sm">
              <span className="text-muted-foreground">Já tem uma conta? </span>
              <Link to="/auth/login" className="text-primary hover:underline">
                Faça login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
